import type { ProductionWorkspaceActualRow, ProductionWorkspacePlanRow } from "../../../shared/production/workspace-report";
import { productionQuantityUnit } from "../../../shared/production/quantity-unit";

export type DailyPlanComparisonRow = {
  key: string;
  productCode: string;
  productName: string;
  unit: string | null;
  hasPlan: boolean;
  productInDailyPlan: boolean;
  planned: number;
  actual: number;
  fulfilled: number | null;
  remaining: number | null;
  excess: number | null;
  needsUnitReview: boolean;
  plannedWorkOrderActual: number;
  otherWorkOrderActual: number;
  planWorkOrders: Set<string>;
  otherWorkOrders: Set<string>;
  planLines: Set<string>;
  actualLines: Set<string>;
  plans: ProductionWorkspacePlanRow[];
  movements: ProductionWorkspaceActualRow[];
};

const amount = (value: number) => Number.parseFloat(value.toPrecision(15));
const workOrderKey = (value: string | null | undefined) => (value ?? "").trim().toLocaleUpperCase("tr-TR");
const unitOf = (value: string | null | undefined) => productionQuantityUnit(value) ?? (value?.trim().toLocaleLowerCase("tr-TR") || null);

/** Product output and order compliance are separate. An order mismatch never hides output. */
export function buildDailyPlanComparison(planRows: ProductionWorkspacePlanRow[], actualRows: ProductionWorkspaceActualRow[]) {
  const result = new Map<string, DailyPlanComparisonRow>();
  const rowFor = (productCode: string, productName: string, unit: string | null) => {
    const key = `${productCode}|${unit ?? "UNKNOWN"}`;
    const row = result.get(key) ?? {
      key, productCode, productName, unit, hasPlan: false, productInDailyPlan: false,
      planned: 0, actual: 0, fulfilled: null, remaining: null, excess: null, needsUnitReview: !unit,
      plannedWorkOrderActual: 0, otherWorkOrderActual: 0,
      planWorkOrders: new Set<string>(), otherWorkOrders: new Set<string>(),
      planLines: new Set<string>(), actualLines: new Set<string>(), plans: [], movements: [],
    };
    result.set(key, row);
    return row;
  };
  for (const item of planRows.filter((item) => item.level === "FINAL_19")) {
    const row = rowFor(item.productCode, item.productName, unitOf(item.unit));
    row.hasPlan = true;
    row.planned = amount(row.planned + item.quantity);
    row.plans.push(item);
    const order = workOrderKey(item.workOrderNo);
    if (order) row.planWorkOrders.add(order);
    if (item.lineName) row.planLines.add(item.lineName);
  }
  for (const item of actualRows.filter((item) => !item.isScrap && item.level === "FINAL_19")) {
    const row = rowFor(item.productCode, item.productName, unitOf(item.unit));
    row.actual = amount(row.actual + item.quantity);
    row.movements.push(item);
    const order = workOrderKey(item.workOrderNo);
    if (row.hasPlan && order && row.planWorkOrders.has(order)) {
      row.plannedWorkOrderActual = amount(row.plannedWorkOrderActual + item.quantity);
    } else {
      row.otherWorkOrderActual = amount(row.otherWorkOrderActual + item.quantity);
      if (order) row.otherWorkOrders.add(order);
    }
    if (item.lineName) row.actualLines.add(item.lineName);
  }
  const rows = [...result.values()];
  for (const row of rows) {
    const siblings = rows.filter((other) => other.productCode === row.productCode);
    row.productInDailyPlan = siblings.some((other) => other.hasPlan);
    // A different/unknown unit cannot prove either zero production or an unplanned product.
    row.needsUnitReview = !row.unit || siblings.some((other) => other.unit !== row.unit &&
      ((row.hasPlan && other.movements.length > 0) || (row.movements.length > 0 && other.hasPlan)));
    if (row.hasPlan && !row.needsUnitReview) {
      row.fulfilled = amount(Math.min(row.planned, row.actual));
      row.remaining = amount(Math.max(0, row.planned - row.actual));
      row.excess = amount(Math.max(0, row.actual - row.planned));
    }
  }
  return rows.sort((left, right) => (right.remaining ?? -1) - (left.remaining ?? -1) || left.productName.localeCompare(right.productName, "tr-TR"));
}

export function summarizeDailyPlanComparison(rows: DailyPlanComparisonRow[]) {
  return [...new Set(rows.flatMap((row) => row.unit ? [row.unit] : []))].map((unit) => {
    const members = rows.filter((row) => row.unit === unit);
    const plannedRows = members.filter((row) => row.hasPlan);
    const sum = (select: (row: DailyPlanComparisonRow) => number) => amount(members.reduce((total, row) => total + select(row), 0));
    const needsUnitReview = plannedRows.some((row) => row.needsUnitReview) ||
      members.some((row) => row.productInDailyPlan && row.needsUnitReview);
    return {
      unit, planned: sum((row) => row.planned), actual: sum((row) => row.actual),
      hasPlan: plannedRows.length > 0, needsUnitReview,
      remaining: needsUnitReview ? null : sum((row) => row.remaining ?? 0),
      excess: needsUnitReview ? null : sum((row) => row.excess ?? 0),
      fulfilled: needsUnitReview ? null : sum((row) => row.fulfilled ?? 0),
      outsidePlan: sum((row) => row.productInDailyPlan ? 0 : row.actual),
    };
  }).sort((left, right) => left.unit.localeCompare(right.unit, "tr-TR"));
}
